import { ChevronDown } from '@/components/ui/icons';
import { useState, useRef } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export type OutputFormat = 'mesh' | 'solidbody' | 'unitcell' | 'implicit';

interface OutputFormatOption {
  id: OutputFormat;
  name: string;
  shortName: string;
  triggerName: string;
  menuName: string;
  description: string;
  homepageDescription?: string;
  comingSoon?: boolean;
  speedTier?: string; // rendered as a non-interactive grey chip, top-right of the row
}

const OUTPUT_FORMAT_OPTIONS: OutputFormatOption[] = [
  {
    // NASA JPL's unitcellengine — lattice-only kernel. Sits at the top of the
    // dropdown because it produces the fastest end-to-end result: closed
    // Python pipeline, no LLM in the loop, geometry returns in seconds for
    // the active topologies. Different stack (Python homogenization-FEA vs
    // PicoGK SDFs) from Implicit Voxel; different scope (meso-scale lattices
    // only vs general-purpose implicit modeling). The user-facing path is
    // the NASA Lattice Primitives sub-tab — free-form prompting is
    // unsupported because unitcellengine has no LLM-driven generator.
    id: 'unitcell',
    name: 'NASA Unitcell Engine',
    shortName: 'Unitcell',
    triggerName: 'NASA Unitcell Engine [.STL]',
    menuName: 'NASA Unitcell Engine [.STL]',
    // speedTier: 'Fastest', // speed labels commented out of functioning — kept for later, not deleted
    description:
      'Award-winning NASA Jet Propulsion Lab mesh engine. Generates meso-scale structural lattice geometry for metal 3D printing. Includes simulation & analysis capabilities. Built on a Signed Distance Field Kernel.',
  },
  {
    id: 'implicit',
    name: 'Implicit Voxel Physics',
    shortName: 'Implicit',
    triggerName: 'Implicit Voxel Physics [.STL]',
    menuName: 'Implicit Voxel Physics [.STL]',
    // speedTier: 'Slowest', // speed labels commented out of functioning — kept for later, not deleted
    description:
      '.STL Files for advanced metal 3D Printing, best for procedurally generated structures in turbomachinery & rocket engines. Utilizing Implicit Modeling and Signed Distance Field technology.',
  },
  {
    id: 'solidbody',
    name: 'Solidbody Physics',
    shortName: 'Solidbody',
    triggerName: 'Solidbody Physics [.STEP] [.STL]',
    menuName: 'Solidbody Physics [.STEP] [.STL]',
    // speedTier: 'Slower', // speed labels commented out of functioning — kept for later, not deleted
    description:
      '.STEP Files, required for most manufacturing processes, like CNC Machining. .STL files cannot be converted to .STEP without expensive Geomagic Software, but .STEP files easily convert. Select .STL in the download dropdown.',
    homepageDescription:
      '.STEP Files, required for most manufacturing processes, like CNC Machining. .STL files cannot be converted to .STEP without expensive Geomagic Software, but .STEP files easily convert. Select .STL in the download dropdown.',
  },
  // Mesh Physics — commented out of functioning (kept for later, do NOT delete).
  // {
  //   id: 'mesh',
  //   name: 'Mesh Physics',
  //   shortName: 'Mesh',
  //   triggerName: 'Mesh Physics [.STL]',
  //   menuName: 'Mesh Physics [.STL]',
  //   speedTier: 'Faster',
  //   description:
  //     '.STL Files for basic polymer 3D Printing, best for complex curves & organic geometry that .STEP struggles with. Cannot be converted to .STEP without expensive Geomagic software.',
  // },
];

interface OutputFormatSelectorProps {
  selectedFormat: OutputFormat;
  onFormatChange: (format: OutputFormat) => void;
  disabled?: boolean;
  className?: string;
  focused?: boolean;
  useShortLabel?: boolean;
  lockedTooltip?: string;
  // Overrides ONLY the trigger label, never the underlying `selectedFormat`
  // (which still drives routing). Mirrors ModelSelector's `displayLabel`.
  // Used by the NASA Unitcell Lattice Converter: it's locked to outputFormat
  // 'unitcell' for routing through the unitcell edge fn, but it actually
  // computes on the PicoGK implicit voxel kernel — so the kernel label must
  // read "Implicit Voxel Physics" without touching the routing value. Only
  // honored when the selector is `disabled` (locked); an open, interactive
  // dropdown always shows the real selected format.
  displayLabel?: string;
}

export function OutputFormatSelector({
  selectedFormat,
  onFormatChange,
  disabled,
  className,
  focused = false,
  useShortLabel = false,
  lockedTooltip,
  displayLabel,
}: OutputFormatSelectorProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const openedWithPointerRef = useRef(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const selectedOption = OUTPUT_FORMAT_OPTIONS.find(
    (o) => o.id === selectedFormat,
  );

  return (
    <Tooltip>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <TooltipTrigger asChild>
          {/* span needed: disabled buttons don't receive pointer events, so tooltip never fires */}
          <span
            className={cn(
              'inline-flex',
              className?.includes('w-full') && 'w-full',
            )}
          >
            <DropdownMenuTrigger asChild>
              <Button
                ref={triggerRef}
                onPointerDown={() => {
                  openedWithPointerRef.current = true;
                }}
                onKeyDown={() => {
                  openedWithPointerRef.current = false;
                }}
                variant="ghost"
                className={cn(
                  'flex h-8 w-auto items-center gap-1.5 rounded-lg px-3 text-sm transition-all duration-200 hover:border-[#107d40]/50 hover:bg-[#107d40]/10',
                  focused
                    ? 'text-white hover:text-white'
                    : 'text-telekinesis-text-secondary hover:text-telekinesis-text-primary',
                  isDropdownOpen &&
                    (focused
                      ? 'bg-telekinesis-neutral-800 text-white'
                      : 'bg-telekinesis-neutral-800 text-telekinesis-text-primary'),
                  // When locked, keep the label fully legible — the disabled cue
                  // is the dimmed chevron below, not a greyed-out value. (Cancels
                  // the Button's base disabled:opacity-50.)
                  'disabled:opacity-100',
                  className,
                )}
                disabled={!!disabled}
              >
                {/* `displayLabel` (when locked) overrides the trigger text on
                    both breakpoints without changing the routing value — e.g.
                    the Lattice Converter shows "Implicit Voxel Physics" while
                    still routing through outputFormat 'unitcell'. */}
                <span className="font-normal text-[#107d40] md:hidden">
                  {displayLabel ?? selectedOption?.shortName}
                </span>
                <span className="hidden font-normal text-[#107d40] md:inline">
                  {displayLabel ??
                    (useShortLabel
                      ? selectedOption?.shortName
                      : selectedOption?.triggerName)}
                </span>
                {/* Solidbody's long label ("Solidbody Physics [.STEP] [.STL]")
                    leaves no room for the chevron — hide it for that selection
                    only, keep it for every other format. */}
                {selectedFormat !== 'solidbody' && (
                  <ChevronDown
                    className={cn(
                      'ml-1 h-4 w-4 flex-shrink-0 transition-transform duration-200',
                      // Dim the chevron (not the label) to signal a locked dropdown.
                      disabled ? 'opacity-30' : 'opacity-70',
                      isDropdownOpen && 'rotate-180',
                    )}
                  />
                )}
              </Button>
            </DropdownMenuTrigger>
          </span>
        </TooltipTrigger>
        <DropdownMenuContent
          className="flex flex-col gap-1 rounded-lg bg-telekinesis-neutral-700 p-1"
          style={{ width: '20rem' }}
          align="start"
          onCloseAutoFocus={(event) => {
            if (openedWithPointerRef.current) {
              event.preventDefault();
              openedWithPointerRef.current = false;
              triggerRef.current?.blur();
            }
          }}
        >
          <DropdownMenuLabel className="px-4 py-2 text-xs uppercase tracking-wider text-telekinesis-text-secondary">
            Physics Kernel
          </DropdownMenuLabel>
          {OUTPUT_FORMAT_OPTIONS.map((option) => {
            const item = (
              <DropdownMenuItem
                key={option.id}
                className={cn(
                  'rounded-md bg-telekinesis-neutral-700 px-4 py-3 transition-colors duration-150 focus:bg-[#107d40]/20',
                  selectedFormat === option.id && 'bg-telekinesis-neutral-800',
                  option.comingSoon ? 'opacity-40' : 'cursor-pointer',
                )}
                disabled={option.comingSoon}
                onClick={(event) => {
                  if (option.comingSoon) return;
                  onFormatChange(option.id);
                  setIsDropdownOpen(false);
                  event.stopPropagation();
                }}
              >
                {/* `relative` anchors the speed-tier tag to this row's box. */}
                <div className="relative flex-1">
                  {/* whitespace-nowrap keeps the title on ONE line (e.g.
                      "Solidbody Physics [.STEP] [.STL]"). The pr-20 speed-tier
                      gutter was dropped since the speed chips are commented out;
                      re-add it if they come back. */}
                  <span
                    className={cn(
                      'block whitespace-nowrap font-medium',
                      focused ? 'text-white' : 'text-telekinesis-text-primary',
                    )}
                  >
                    {useShortLabel ? option.name : option.menuName}
                  </span>
                  {!useShortLabel && option.speedTier && (
                    <span
                      aria-hidden="true"
                      className={cn(
                        // Grey rectangle styled like the File/Settings menu-bar
                        // buttons' hover span (AppMenuBar.tsx:216): `rounded`
                        // square-ish corners + bg-telekinesis-neutral-700. NOT a
                        // pill. Text matches the kernel title exactly (same
                        // inherited size, same weight + color). Pinned to the
                        // box's top-right corner, in line with the title text.
                        // pointer-events-none + aria-hidden so it stays
                        // non-interactive inside the clickable DropdownMenuItem.
                        'pointer-events-none absolute right-0 top-0 select-none rounded bg-telekinesis-neutral-700 px-2 py-0.5 font-medium',
                        focused
                          ? 'text-white'
                          : 'text-telekinesis-text-primary',
                      )}
                    >
                      {option.speedTier}
                    </span>
                  )}
                  <p
                    className={cn(
                      'mt-0.5 text-left text-xs leading-snug',
                      focused ? 'text-white' : 'text-gray-400',
                    )}
                  >
                    {useShortLabel
                      ? option.description
                      : (option.homepageDescription ?? option.description)}
                  </p>
                </div>
              </DropdownMenuItem>
            );

            if (option.comingSoon) {
              return (
                <Tooltip key={option.id}>
                  <TooltipTrigger asChild>
                    {/* span needed: disabled elements don't receive pointer events */}
                    <span>{item}</span>
                  </TooltipTrigger>
                  <TooltipContent>Coming soon.</TooltipContent>
                </Tooltip>
              );
            }

            return item;
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      <TooltipContent side="bottom" align="start">
        {lockedTooltip ?? 'Select Physics Model'}
      </TooltipContent>
    </Tooltip>
  );
}
